import { useEffect, useMemo, type MutableRefObject } from 'react';
import { useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleMutationBlocked,
  selectScheduleData,
  selectScheduleConstraints,
  selectScheduleCurrentFilters,
  selectScheduleClassIdToObject,
  selectUnscheduledMatches,
  selectScheduleTournamentInfo,
} from '../../../../../features/schedule/scheduleSelectors';
import { getTournamentTimezone } from '../schedule.helpers';
import type { ScheduleLeftRailTab } from './ScheduleLeftRail';
import { selectScheduleGridDayInput } from '../../../../../features/schedule/scheduleGridSelectors';
import { deriveDisplayScheduleView, resolveScheduleDayKey } from '../scheduleDisplayView';
import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import type { ScheduleZoomFloors } from '../../../../../features/schedule/scheduleZoomBounds';
import { isScheduleHistoryKeyTarget } from './scheduleLayoutHooks';
import { useSchedulePlacementState } from './schedulePlacementState';
import { ScheduleWorkbench } from './ScheduleWorkbench';

type ScheduleDnDShellProps = {
  selectedDayKey: number;
  queueWheelZoom: (zoomIn: boolean, clientX: number, clientY: number) => void;
  onDayZoomFloorsChange: (dayKey: number, floors: ScheduleZoomFloors) => void;
  isMobile: boolean;
  lastGridPointerRef: MutableRefObject<{ clientX: number; clientY: number } | null>;
  clearPlacementRef: MutableRefObject<(() => void) | null>;
  showScheduleConflicts: boolean;
  conflictMatchIdList: number[];
  blockInvalidPlacement: boolean;
  leftRailTab: ScheduleLeftRailTab;
  onLeftRailTabChange: (tab: ScheduleLeftRailTab) => void;
  onSelectedDayKeyChange: (dayKey: number) => void;
  focusedConflictMatchId: number | null;
  focusedConflictPeerIds: number[];
  conflictFocusPulseGeneration: number;
  onFocusConflictOnGrid: (conflict: ScheduleConflict) => void;
  onViewConflictMatch: (matchId: number) => void;
};

export function ScheduleDnDShell({
  selectedDayKey,
  queueWheelZoom,
  onDayZoomFloorsChange,
  isMobile,
  lastGridPointerRef,
  clearPlacementRef,
  showScheduleConflicts,
  conflictMatchIdList,
  blockInvalidPlacement,
  leftRailTab,
  onLeftRailTabChange,
  onSelectedDayKeyChange,
  focusedConflictMatchId,
  focusedConflictPeerIds,
  conflictFocusPulseGeneration,
  onFocusConflictOnGrid,
  onViewConflictMatch,
}: ScheduleDnDShellProps) {
  const schedule = useAppSelector(selectScheduleData);
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);
  const currentFilters = useAppSelector(selectScheduleCurrentFilters);
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);
  const scheduleTimeZone = getTournamentTimezone(
    tournamentInfo?.country,
    tournamentInfo?.timeZone
  );

  const effectiveSelectedDayKey = useMemo(
    () => resolveScheduleDayKey(schedule, selectedDayKey),
    [schedule, selectedDayKey]
  );

  const gridInput = useAppSelector((state) =>
    selectScheduleGridDayInput(state, effectiveSelectedDayKey)
  );

  const displayView = useMemo(
    () => deriveDisplayScheduleView(schedule, currentFilters, effectiveSelectedDayKey),
    [schedule, currentFilters, effectiveSelectedDayKey]
  );

  const displayViewStructureKey = useMemo(
    () =>
      displayView?.days.map((d) => `${d.dayKey}:${d.courts.length}`).join('|') ?? '',
    [displayView?.days]
  );

  const placementIndexDays = useMemo(() => {
    if (gridInput?.day) return [gridInput.day];
    return displayView?.days ?? [];
  }, [gridInput?.day, displayView?.days]);

  const placement = useSchedulePlacementState({
    schedule,
    displayView,
    displayViewStructureKey,
    placementIndexDays,
    gridInput,
    scheduleConstraints,
    currentFilters,
    unscheduledMatches,
    scheduleMutationBlocked,
    blockInvalidPlacement,
    scheduleTimeZone,
    classIdToObject,
  });

  useEffect(() => {
    clearPlacementRef.current = () => placement.setClickPlacement(null);
    return () => {
      clearPlacementRef.current = null;
    };
  }, [clearPlacementRef, placement.setClickPlacement]);

  useEffect(() => {
    const gridEl = placement.scheduleGridWheelRef.current;
    if (!gridEl) return;

    const onMouseMove = (e: MouseEvent) => {
      lastGridPointerRef.current = { clientX: e.clientX, clientY: e.clientY };
    };

    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (isScheduleHistoryKeyTarget(e.target)) return;
      if (!displayView || !scheduleConstraints) return;
      if (e.deltaY === 0) return;

      e.preventDefault();
      queueWheelZoom(e.deltaY < 0, e.clientX, e.clientY);
    };

    gridEl.addEventListener('mousemove', onMouseMove);
    gridEl.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      gridEl.removeEventListener('mousemove', onMouseMove);
      gridEl.removeEventListener('wheel', onWheel);
    };
  }, [
    displayView,
    scheduleConstraints,
    queueWheelZoom,
    placement.scheduleGridWheelRef,
    lastGridPointerRef,
  ]);

  if (!displayView || !scheduleConstraints) {
    return null;
  }

  return (
    <ScheduleWorkbench
      selectedDayKey={effectiveSelectedDayKey}
      onSelectedDayKeyChange={onSelectedDayKeyChange}
      scheduleMutationBlocked={scheduleMutationBlocked}
      placement={placement}
      onDayZoomFloorsChange={onDayZoomFloorsChange}
      isMobile={isMobile}
      showScheduleConflicts={showScheduleConflicts}
      conflictMatchIdList={conflictMatchIdList}
      blockInvalidPlacement={blockInvalidPlacement}
      schedule={schedule}
      scheduleConstraints={scheduleConstraints ?? null}
      scheduleTimeZone={scheduleTimeZone}
      classIdToObject={classIdToObject}
      leftRailTab={leftRailTab}
      onLeftRailTabChange={onLeftRailTabChange}
      focusedConflictMatchId={focusedConflictMatchId}
      focusedConflictPeerIds={focusedConflictPeerIds}
      conflictFocusPulseGeneration={conflictFocusPulseGeneration}
      onFocusConflictOnGrid={onFocusConflictOnGrid}
      onViewConflictMatch={onViewConflictMatch}
    />
  );
}
